/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useRef, useEffect, useMemo } from "react";

import moment from "moment-timezone";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

import { Bar, Chart } from "react-chartjs-2";

//Backend access
import { loadToken, getCameraHistory } from 'services/api/bks';
import PropTypes from "prop-types";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import CameraImage from "components/Camera/CameraImage";
import { PacmanLoader } from "react-spinners";
import { Chart as Chartjs, registerables } from 'chart.js';
import zoomPlugin from "chartjs-plugin-zoom";
import { Card, Icon } from "@mui/material";
import { loadingScreen } from "utils/wrapper";
import { useSoftUIController } from "hooks/context";
import { PhotoProvider, PhotoView } from "react-photo-view";
import DetectionImage from "components/Camera/DetectionImage";
import CameraLatest from "components/Camera/CameraLatest";

import ParkingValidation from "assets/json/ParkingValidation.json";
import SoftButton from "components/SoftComponents/SoftButton";

Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

var timeout, current_chart;

function MobileDirectionalPad({ onMove }) {
  // Common style for all directional buttons
  const buttonStyles = {
    width: "80px",
    height: "50px",
    minWidth: "80px",
    borderRadius: "8px", // Matches Soft UI "lg"
    padding: 0,
    fontSize: "1.5rem",
  };

  return (
    <SoftBox py={1}
      display="flex"
      justifyContent="center"
      sx={{
        width: "200px",
        margin: "0 auto",
        touchAction: "none" // Prevents browser scroll while clicking
      }}
    >
      <SoftBox display="flex" justifyContent="center" sx={{ gap: 2 }}>
        <SoftButton variant="gradient" color="info" sx={buttonStyles} onClick={() => onMove("left")}>
          <Icon>keyboard_arrow_left</Icon>
        </SoftButton>
        <SoftButton variant="gradient" color="info" sx={buttonStyles} onClick={() => onMove("right")}>
          <Icon>keyboard_arrow_right</Icon>
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

// Images
function CameraFeedChart({ device, startTime, endTime, setStartTime, setEndTime, chartData, isLoading, setIsLoading, directionTrigger }) {
  const [selectedImage, setSelectedImage] = useState({
    data: chartData[Number(chartData.length) - 1] ? chartData[Number(chartData.length) - 1] : null,
    index: chartData[Number(chartData.length) - 1] ? Number(chartData.length) - 1 : Infinity
  });

  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(null);

  loadToken(useAuth0());
  const [controller] = useSoftUIController();
  const { deviceLayout } = controller;

  function nextImage() {
    var new_image = {
      index: selectedImage.index,
      data: selectedImage.data
    };

    new_image.index++;

    if (new_image.index == -1) {
      new_image.index = chartData.length - 1;
    }
    else if (chartData && new_image.index >= chartData.length) {
      new_image.index = chartData.length - 1;
    }

    if (typeof chartData != 'undefined' && typeof chartData[new_image.index] != 'undefined') {
      new_image.data = chartData[new_image.index];
    }

    setSelectedImage(new_image);
  }

  function previousImage() {
    var new_image = {
      index: selectedImage.index,
      data: selectedImage.data
    };

    new_image.index--;

    if (new_image.index == -1) {
      new_image.index = 0;
    }
    else if (new_image.index >= chartData.length) {
      new_image.index = chartData.length - 1;
    }

    if (typeof chartData != 'undefined' && typeof chartData[new_image.index] != 'undefined') {
      new_image.data = chartData[new_image.index];
    }

    setSelectedImage(new_image);

  }

  // function handleKeyDown(e) {
  //   // arrow up/down button should select next/previous list element
  //   if (e.keyCode === 37) {
  //     previousImage();
  //   } else if (e.keyCode === 39) {
  //     nextImage();
  //   }
  // }

  useEffect(() => {
    if (directionTrigger?.next > 0) {
      console.log('next')
      nextImage();
    }
  }, [directionTrigger?.next]);

  useEffect(() => {
    if (directionTrigger?.prev > 0) {
      previousImage();
    }
  }, [directionTrigger?.prev]);

  function onMove(moveType) {
    if (moveType == "left") {
      previousImage();
    }
    else if (moveType == "right") {
      nextImage();
    }
  }

  // console.log(data)
  const scrollEvent = (chart) => {
    // console.log(timeout);
    current_chart = chart;
    if (typeof timeout != 'undefined') {
      clearTimeout(timeout);
      setIsLoading(false);
      timeout = undefined;
    }

    timeout = setTimeout(() => {
      runWheelScrollEvent(current_chart);
      setIsLoading(true);
      timeout = undefined;
    }, 400);
  }

  const runWheelScrollEvent = ({ chart }) => {
    var start_time, end_time;
    let min_limit = endTime.clone().subtract(7, 'day');
    let max_limit = startTime.clone().add(7, 'day');
    // console.log(chart.scales.x.min)
    let min_moment = moment(chart.scales.x.min);
    let max_moment = moment(chart.scales.x.max);

    if (min_moment.isBefore(min_limit)) {
      start_time = min_limit;
    }
    else {
      start_time = min_moment;
    }

    if (max_moment.isAfter(max_limit)) {
      end_time = max_limit;
    }
    else {
      end_time = max_moment;
    }

    setChartWidth(chart.width);
    setStartTime(start_time);
    setEndTime(end_time);

  };

  const alternatingDayShadingPlugin = {
    id: 'alternatingDayShading',
    beforeDraw: (chart) => {
      const { ctx, chartArea, scales: { x } } = chart;

      // 1. Get the start of the visible range, snapped to the beginning of that day
      let current = moment(x.min).startOf('day');
      const chartEnd = moment(x.max);

      ctx.save();

      // 2. Loop through each day visible in the current zoom/view
      while (current.isBefore(chartEnd)) {

        // 3. Logic: Shade every second day (Even-numbered days of the year)
        // Use .dayOfYear() for a consistent alternating sequence
        if (current.dayOfYear() % 2 === 0) {

          // Find the pixel position for the start of this day and start of next
          const left = x.getPixelForValue(current.valueOf());
          const nextDay = moment(current).add(1, 'days').valueOf();
          const right = x.getPixelForValue(nextDay);

          // 4. Draw the shading
          ctx.fillStyle = 'rgba(0, 100, 255, 0.06)'; // Subtle blue

          // Clip the rectangle so it stays inside the chart area boundaries
          const drawX = Math.max(left, chartArea.left);
          const drawRight = Math.min(right, chartArea.right);
          const drawWidth = drawRight - drawX;

          if (drawWidth > 0) {
            ctx.fillRect(drawX, chartArea.top, drawWidth, chartArea.bottom - chartArea.top);
          }
        }

        // Move to the next day
        current.add(1, 'days');
      }

      ctx.restore();
    }
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: 0
    },
    scales: {
      x: {
        type: 'time',
        min: startTime.unix() * 1000,
        max: endTime.unix() * 1000,
        time: {
          displayFormats: {
            hour: 'h:mm a'
          }
        },
        grid: {
          color: (context) => {
            if (!context.tick) return 'rgba(0, 0, 0, 0.05)';

            const m = moment(context.tick.value);
            return (m.dayOfYear() % 2 === 0)
              ? 'rgba(0, 0, 0, 0.1)'
              : 'rgba(0, 100, 255, 0.1)';
          },
          z: -1,

        },
        ticks: {
          source: 'auto',
          major: {
            enabled: true
          },
          time: {
            unit: 'minute'
          },
          callback: function (value, index, ticks) {
            const m = moment(ticks[index].value);
            const timeLabel = m.format('h:mm a');

            if (m.hour() === 0 && m.minute() === 0) {
              return [timeLabel, m.format('dddd').toUpperCase()];
            }
            return value;
          }
        }
      },
      y: {
        ticks: {
          // Include a dollar sign in the ticks
          callback: function (value, index, ticks) {
            if (Math.floor(value) === value) {
              if (value == -1) {
                return "";
              }
              return value;
            }
          }
        },
        min: -1
      }
    },
    fill: true,
    borderJoinStyle: "bevel",
    tension: 0.2,
    grouped: true,
    plugins: {
      zoom: {
        zoom: {
          mode: 'x',
          drag: {
            enabled: false,
          },
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: false,
          },
          speed: 0.5, sensitivity: 0.5,
          onZoomComplete: scrollEvent,
        },
        limits: {
          x: {
            min: 0,
            max: moment().unix() * 1000,
            minRange: 5000 * 1000,
          }
        }
      },
      tooltip: {
        events: ["click"],
        callbacks: {
          label: function (context) {

            // debugger;
            if (context.raw && context.raw.data) {

              if (!selectedImage.data || context.raw.data._id != selectedImage.data._id) {
                // console.log(context)
                setSelectedImage({
                  index: context.dataIndex,
                  data: context.raw.data
                });
              }
            }

          },
        }
      },
      legend: {
        position: 'top',
        display: false
      }
    }
  }
  var chart_data = {
    datasets: []
  }

  if (chartWidth || chartRef.current) {
    var width = chartWidth ? chartWidth : chartRef.current.width;
    var barThickness = (Number(width) / Number(chartData.length));
    if (barThickness > 100) {
      barThickness = 30;
    }
    if (barThickness > 10) {
      barThickness -= 4;
    }
    else {
      barThickness -= 1;
    }
  }
  else {
    var barThickness = 40;
  }

  chart_data.datasets.push({
    label: device.uuid,
    backgroundColor: function (color) {
      try {
        if (selectedImage.data && selectedImage.data._id == color.raw.data._id) {
          return "blue";
        }
        else {
          return "lightgrey"
        }
      }
      catch (e) {
        return "lightgrey"
      }
    },
    data: chartData.map(function (row) {
      return { 'x': row.timestamp * 1000, 'y': [-1, row.objects.length || 0], 'data': row }
    }),
    barThickness: barThickness
  })

  const chart = (<Bar
    ref={chartRef}
    data={chart_data}
    options={options}
    plugins={[alternatingDayShadingPlugin]}
  />);

  return (
    <SoftBox display="flex" justifyContent="center" alignItems="center" width="100%">
      <Card sx={{ width: "100%", height: deviceLayout == "mobile" ? "auto" : "calc(100svh - 13.75rem - 16px)", overflowY: 'auto' }}>
        <SoftBox
          p={2}
          // onKeyDown={handleKeyDown}
          tabIndex={0}
          display="flex"
          flexDirection="column"
          maxHeight="calc(100% - 82px)"
        >
          <SoftBox
            width="100%"
            sx={{
              flex: "1 1 auto",
              minHeight: deviceLayout == "mobile" ? '400px' : '400px',
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              // maxHeight: 'fit-content'
              aspectRatio: "16 / 9",
            }}
          >
            {(isLoading || selectedImage.data == null) ? (
              <>
                {isLoading ? (
                  <>
                    {loadingScreen(deviceLayout, false)}
                  </>
                ) : (
                  <CameraLatest uuid={device.uuid} line_coordinates={device.organisation_id == "translink-poc" ? ParkingValidation["locations"][device.stop.site_id]["line"] : false} />
                )}
              </>
            ) : (
              <>
                {device.organisation_id == "translink-poc" ? (
                  <DetectionImage image_id={selectedImage.data._id} device={device} />
                ) : (
                  <CameraImage image_id={selectedImage.data._id} />
                )}
              </>
            )}

          </SoftBox>
          <SoftBox
            sx={{
              pt: 2,
              width: "100%"
            }}
          >
            {deviceLayout == 'mobile' && (
              <MobileDirectionalPad onMove={onMove} />
            )}
            <SoftBox
              flexShrink={0}
              pt={2}
              sx={{
                height: deviceLayout == "mobile" ? "25svh" : "30svh",
                minHeight: deviceLayout == "mobile" ? "150px" : "300px",
                width: "100%"
              }}
            >
              {chart}
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Card>
    </SoftBox>
  );
}

// Typechecking props for the CameraFeed
CameraFeedChart.propTypes = {
  device: PropTypes.object,
  startTime: PropTypes.object,
  setStartTime: PropTypes.func,
  endTime: PropTypes.object,
  setEndTime: PropTypes.func,
  chartData: PropTypes.object,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  directionTrigger: PropTypes.object
};


export default CameraFeedChart;
